/* eslint-disable react/no-array-index-key */
// TODO: Tech Debt: refactor this component into smaller components
import { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  Chip,
  TableContainer,
  Table,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
} from "@mui/material";
import {
  Facebook as FacebookIcon,
  Check as CheckIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  InfoOutlined as InfoIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { getMetaAccounts, getMetaFormsByAccountId, getMetaFormById } from "../../api/Meta";
import AutoCompleteDropdown from "../primitives/AutoCompleteDropdown";
import { getCountries } from "../../api/Countries";
import { addConfiguration } from "../../api/ConfigService";
import { TikTokIcon } from "./icons/icons";
import TextField from "../primitives/TextField";
import Dropdown from "../primitives/Dropdown";
import { useAlert } from "../../hook/useAlert";
import Button from "../primitives/Button";
import GoogleLogo from "../../assets/images/google-logo.png";
import SnapchatLogo from "../../assets/images/snapchat-logo.png";

const steps = ["Select Provider", "Select Account & Form", "Form Details", "Review & Submit"];

const filterEnrichmentTypesByCategory = (selectedCategory, enrichmentTypes) => {
  if (selectedCategory === "Consumption") {
    return enrichmentTypes.filter((type) => type.value === "weekly_plus");
  }
  if (selectedCategory === "Interest") {
    return enrichmentTypes.filter(
      (type) => type.value === "passion_points" || type.value === "brand_preferences"
    );
  }
  return enrichmentTypes;
};

const SocialConfigWizard = () => {
  const { onError, onSuccess, onInfo } = useAlert();

  const [activeStep, setActiveStep] = useState(0);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [forms, setForms] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [selectedForm, setSelectedForm] = useState("");
  const [formDetails, setFormDetails] = useState(null);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [pageAccessToken, setPageAccessToken] = useState("");
  const [enrichments, setEnrichments] = useState([]);
  const [defaultEnrichments, setDefaultEnrichments] = useState([]);
  const [loadingState, setLoadingState] = useState({
    accounts: false,
    forms: false,
    formDetails: false,
    submit: false,
  });

  const handleLoadForms = async (accountId = selectedAccount, token = pageAccessToken) => {
    if (!accountId || !token) {
      onError("Please select an account with a valid access token");
      return;
    }

    try {
      setLoadingState((prev) => ({ ...prev, forms: true }));
      const formsData = await getMetaFormsByAccountId(accountId, token);
      setForms(formsData.data);
      onSuccess("Forms loaded successfully");
    } catch (error) {
      onError("Failed to load forms");
    } finally {
      setLoadingState((prev) => ({ ...prev, forms: false }));
    }
  };

  const handleLoadAccounts = async () => {
    try {
      setLoadingState((prev) => ({ ...prev, accounts: true }));
      const accountsData = await getMetaAccounts();
      setAccounts(accountsData.data);
      onSuccess("Accounts loaded successfully");
    } catch (error) {
      onError("Failed to load accounts. Please try again.");
    } finally {
      setLoadingState((prev) => ({ ...prev, accounts: false }));
    }
  };

  const handleProviderSelect = (provider) => {
    setSelectedProvider(provider);

    setAccounts([]);
    setForms([]);
    setSelectedAccount("");
    setSelectedForm("");
    setFormDetails(null);
    setPageAccessToken("");
    setEnrichments([]);
    setDefaultEnrichments([]);
    setSelectedCountry("");

    if (provider === "facebook") {
      handleLoadAccounts();
    }

    setActiveStep(1);
  };

  const handleAccountChange = (e) => {
    const accountId = e.target.value;
    setSelectedAccount(accountId);
    setForms([]);
    setSelectedForm("");
    setFormDetails(null);

    const account = accounts.find((acc) => acc.id.toString() === accountId);
    if (account && account.access_token) {
      setPageAccessToken(account.access_token);
      handleLoadForms(accountId, account.access_token);
    } else {
      onError("Access token not available for selected account");
      setPageAccessToken("");
    }
  };

  const handleFormChange = (e) => {
    setSelectedForm(e.target.value);
    setFormDetails(null);
  };

  const handleCountryChange = (e) => {
    setSelectedCountry(e.target.value);
  };

  const handleLoadFormDetails = async () => {
    if (!selectedForm) {
      onInfo("Please select a form first");
      return;
    }

    try {
      setLoadingState((prev) => ({ ...prev, formDetails: true }));
      const formData = await getMetaFormById(selectedForm);
      setFormDetails(formData);

      let initialEnrichments = [];
      if (formData.questions) {
        initialEnrichments = formData.questions
          .filter((q) => q.options && q.options.length > 0)
          .map((q) => ({
            questionId: q.id,
            category: "",
            type: "",
            header: q.key,
            enum: q.options.map((option) => option.key),
          }));
        setEnrichments(initialEnrichments);
      }

      if (!initialEnrichments.length && !defaultEnrichments.length) {
        setActiveStep(3);
      } else {
        setActiveStep(2);
      }

      onSuccess("Form details loaded successfully");
    } catch (error) {
      onError("Failed to load form details");
    } finally {
      setLoadingState((prev) => ({ ...prev, formDetails: false }));
    }
  };

  const handleEnrichmentChange = (questionId, field, value) => {
    setEnrichments((prevEnrichments) =>
      prevEnrichments.map((e) => {
        if (e.questionId === questionId) {
          if (field === "category") {
            const enrichmentTypes = [
              { value: "brand_preferences", display: "Brand Preference" },
              { value: "passion_points", display: "Passion Point" },
              { value: "weekly_plus", display: "Weekly Plus" },
            ];
            const filteredTypes = filterEnrichmentTypesByCategory(value, enrichmentTypes);
            const preSelectedType = filteredTypes.length === 1 ? filteredTypes[0].value : "";
            return { ...e, [field]: value, type: preSelectedType };
          }
          return { ...e, [field]: value };
        }
        return e;
      })
    );
  };

  const handleAddDefaultEnrichment = () => {
    if (defaultEnrichments.length >= 3) {
      onInfo("Maximum of 3 default enrichments allowed");
      return;
    }

    const newEnrichment = {
      id: Date.now(),
      category: "",
      type: "",
      header: "",
      value: "",
      isDefault: true,
    };
    setDefaultEnrichments((prev) => [...prev, newEnrichment]);
  };

  const handleRemoveDefaultEnrichment = (id) => {
    setDefaultEnrichments((prev) => prev.filter((e) => e.id !== id));
  };

  const handleDefaultEnrichmentChange = (id, field, value) => {
    setDefaultEnrichments((prev) =>
      prev.map((e) => {
        if (e.id === id) {
          if (field === "category") {
            const enrichmentTypes = [
              { value: "brand_preferences", display: "Brand Preference" },
              { value: "passion_points", display: "Passion Point" },
              { value: "weekly_plus", display: "Weekly Plus" },
            ];
            const filteredTypes = filterEnrichmentTypesByCategory(value, enrichmentTypes);
            const preSelectedType = filteredTypes.length === 1 ? filteredTypes[0].value : "";
            return { ...e, [field]: value, type: preSelectedType, header: preSelectedType };
          }
          if (field === "type") {
            return { ...e, [field]: value, header: value };
          }
          return { ...e, [field]: value };
        }
        return e;
      })
    );
  };

  const handleSubmit = async () => {
    const combinedEnrichments = [
      ...enrichments.filter((e) => e.category && e.type),
      ...defaultEnrichments.filter((e) => e.category && e.type && e.value),
    ];

    const dataToSubmit = {
      platform: selectedProvider,
      country: selectedCountry,
      accountId: selectedAccount,
      formId: selectedForm,
      formDetails,
      pageAccessToken,
      enrichments: combinedEnrichments,
      defaultEnrichments: defaultEnrichments.filter((e) => e.category && e.type && e.value),
      userName: sessionStorage.getItem("userName") || "anonymous",
    };

    try {
      setLoadingState((prev) => ({ ...prev, submit: true }));
      const result = await addConfiguration(dataToSubmit);

      if (result.success) {
        onSuccess(
          `Configuration added successfully: ${result.data.message} - ${result.data.fileName}`
        );
        setActiveStep(4);
      } else if (result.isConflict) {
        onInfo(result.message);
      }
    } catch (error) {
      onError(error.message);
    } finally {
      setLoadingState((prev) => ({ ...prev, submit: false }));
    }
  };

  const handleNext = () => {
    if (activeStep === 1) {
      if (!selectedForm) {
        onInfo("Please select a form first");
        return;
      }
      if (!selectedCountry) {
        onInfo("Please select a country first");
        return;
      }
      handleLoadFormDetails();
    } else if (activeStep === 2) {
      setActiveStep(3);
    } else if (activeStep === 3) {
      handleSubmit();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 3 && !enrichments.length && !defaultEnrichments.length) {
      setActiveStep(1);
    } else {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedProvider("");
    setSelectedAccount("");
    setSelectedForm("");
    setFormDetails(null);
    setSelectedCountry("");
    setEnrichments([]);
    setDefaultEnrichments([]);
    setAccounts([]);
    setForms([]);
    setPageAccessToken("");
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const countryList = await getCountries();
        setCountries(countryList.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (error) {
        onError("Failed to load countries. Please try again.");
      }
    };

    if (!countries.length) {
      fetchCountries();
    }
  }, []);

  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <ProviderSelectionStep
            selectedProvider={selectedProvider}
            onSelect={handleProviderSelect}
          />
        );
      case 1:
        return (
          <AccountFormSelectionStep
            provider={selectedProvider}
            accounts={accounts}
            forms={forms}
            selectedAccount={selectedAccount}
            selectedForm={selectedForm}
            onAccountChange={handleAccountChange}
            onFormChange={handleFormChange}
            onLoadAccounts={handleLoadAccounts}
            onLoadForms={handleLoadForms}
            loadingState={loadingState}
            countries={countries}
            selectedCountry={selectedCountry}
            onCountryChange={handleCountryChange}
          />
        );
      case 2:
        return (
          <FormDetailsStep
            formDetails={formDetails}
            countries={countries}
            selectedCountry={selectedCountry}
            onCountryChange={handleCountryChange}
            enrichments={enrichments}
            onEnrichmentChange={handleEnrichmentChange}
            defaultEnrichments={defaultEnrichments}
            onAddDefaultEnrichment={handleAddDefaultEnrichment}
            onRemoveDefaultEnrichment={handleRemoveDefaultEnrichment}
            onDefaultEnrichmentChange={handleDefaultEnrichmentChange}
            isLoading={loadingState.formDetails}
          />
        );
      case 3:
        return (
          <ReviewStep
            provider={selectedProvider}
            account={
              accounts.find((a) => a.id.toString() === selectedAccount)?.name || selectedAccount
            }
            form={forms.find((f) => f.id === selectedForm)?.name || selectedForm}
            country={countries.find((c) => c.code === selectedCountry)?.name || selectedCountry}
            enrichments={enrichments}
            defaultEnrichments={defaultEnrichments}
          />
        );
      case 4:
        return <SuccessStep onReset={handleReset} />;
      default:
        return "Unknown step";
    }
  };

  const isAnyLoading = Object.values(loadingState).some((loading) => loading);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ width: "100%", py: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Box sx={{ mt: 4, mb: 2 }}>{getStepContent()}</Box>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            {activeStep > 0 && activeStep < 4 && (
              <Button
                variant="outlined"
                startIcon={<BackIcon />}
                onClick={handleBack}
                disabled={isAnyLoading}
                name="Back"
              />
            )}
            {activeStep < 3 && (
              <Box sx={{ ml: "auto" }}>
                <Button
                  variant="contained"
                  color="primary"
                  endIcon={loadingState.formDetails ? <CircularProgress size={16} /> : <NextIcon />}
                  onClick={handleNext}
                  name="Next"
                  disabled={
                    isAnyLoading ||
                    (activeStep === 0 && !selectedProvider) ||
                    (activeStep === 1 && (!selectedForm || !selectedCountry))
                  }
                />
              </Box>
            )}
            {activeStep === 3 && (
              <Box sx={{ ml: "auto" }}>
                <Button
                  variant="contained"
                  color="success"
                  endIcon={loadingState.submit ? <CircularProgress size={16} /> : <CheckIcon />}
                  onClick={handleSubmit}
                  disabled={loadingState.submit}
                  name="Submit"
                />
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
      {/* <Box sx={{ textAlign: "right", py: 2 }}>Form created by the agency.</Box> */}
    </Container>
  );
};

const ProviderSelectionStep = ({ selectedProvider, onSelect }) => {
  const providers = [
    {
      id: "facebook",
      name: "Facebook",
      icon: <FacebookIcon sx={{ fontSize: 36 }} />,
      color: "#4267B2",
    },
    {
      id: "tiktok",
      name: "TikTok",
      icon: <TikTokIcon sx={{ fontSize: 36 }} />,
      color: "#000000",
    },
    {
      id: "snapchat",
      name: "Snapchat",
      icon: (
        <Box
          component="img"
          src={SnapchatLogo}
          alt="Snapchat"
          sx={{ height: 50, width: "auto", marginBottom: "-13px" }}
        />
      ),
      color: "#FFDE03",
    },
    {
      id: "google",
      name: "Google",
      icon: <Box component="img" src={GoogleLogo} alt="Google" sx={{ height: 32 }} />,
      color: "#4285F4",
    },
  ];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Select a Provider
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          gap: 2,
        }}
      >
        {providers.map((provider) => (
          <Card
            key={provider.id}
            onClick={() => onSelect(provider.id)}
            sx={{
              flex: 1,
              cursor: "pointer",
              height: 140,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              border:
                selectedProvider === provider.id
                  ? `2px solid ${provider.color}`
                  : "1px solid #e0e0e0",
              backgroundColor: selectedProvider === provider.id ? `${provider.color}10` : "white",
              "&:hover": {
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                borderColor: provider.color,
              },
            }}
          >
            {selectedProvider === provider.id && (
              <CheckIcon color="success" sx={{ position: "absolute", top: 10, right: 10 }} />
            )}
            <Box
              sx={{
                color: provider.color,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: 2,
              }}
            >
              {provider.icon}
              <Typography
                variant="subtitle1"
                sx={{
                  mt: 2,
                  textAlign: "center",
                  fontWeight: selectedProvider === provider.id ? 600 : 400,
                }}
              >
                {provider.id === "google" ? (
                  <Box component="span" sx={{ fontFamily: "'Product Sans', Arial, sans-serif" }}>
                    <Box component="span" sx={{ color: "#4285F4" }}>
                      G
                    </Box>
                    <Box component="span" sx={{ color: "#EA4335" }}>
                      o
                    </Box>
                    <Box component="span" sx={{ color: "#FBBC05" }}>
                      o
                    </Box>
                    <Box component="span" sx={{ color: "#4285F4" }}>
                      g
                    </Box>
                    <Box component="span" sx={{ color: "#34A853" }}>
                      l
                    </Box>
                    <Box component="span" sx={{ color: "#EA4335" }}>
                      e
                    </Box>
                  </Box>
                ) : (
                  provider.name
                )}
              </Typography>
            </Box>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

const AccountFormSelectionStep = ({
  provider,
  accounts,
  forms,
  selectedAccount,
  selectedForm,
  onAccountChange,
  onFormChange,
  onLoadAccounts,
  onLoadForms,
  loadingState,
  countries,
  selectedCountry,
  onCountryChange,
}) => {
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Account & Form
      </Typography>
      {provider === "facebook" ? (
        <Box sx={{ my: 2 }}>
          <AutoCompleteDropdown
            label="Select Country"
            id="country-select"
            name="country"
            value={selectedCountry}
            onChange={onCountryChange}
            options={countries.map((country) => ({
              value: country.code,
              label: `${country.name} (${country.code})`,
            }))}
          />
          {loadingState.accounts ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2">Loading accounts...</Typography>
            </Box>
          ) : accounts.length > 0 ? (
            <AutoCompleteDropdown
              label="Select Account ID"
              id="account-select"
              name="account"
              value={selectedAccount}
              onChange={onAccountChange}
              options={accounts.map((account) => ({
                value: account.id.toString(),
                label: `${account.name} (${account.id})`,
              }))}
              searchPlaceholder="Search accounts..."
            />
          ) : (
            <Button
              variant="contained"
              onClick={onLoadAccounts}
              disabled={loadingState.accounts}
              startIcon={<RefreshIcon />}
              name="Retry Loading Accounts"
            />
          )}
          {loadingState.forms ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2">Loading forms...</Typography>
            </Box>
          ) : forms.length > 0 ? (
            <AutoCompleteDropdown
              label="Select Form"
              id="form-select"
              name="form"
              value={selectedForm}
              onChange={onFormChange}
              options={forms.map((form) => ({
                value: form.id,
                label: `${form.name} (${form.id})`,
              }))}
              searchPlaceholder="Search forms..."
            />
          ) : selectedAccount ? (
            <Button
              variant="contained"
              onClick={onLoadForms}
              disabled={loadingState.forms}
              startIcon={<RefreshIcon />}
              name="Retry Loading Forms"
            />
          ) : null}
        </Box>
      ) : (
        <Typography variant="body1">Coming Soon</Typography>
      )}
    </Box>
  );
};

const FormDetailsStep = ({
  formDetails,
  enrichments,
  onEnrichmentChange,
  defaultEnrichments,
  onAddDefaultEnrichment,
  onRemoveDefaultEnrichment,
  onDefaultEnrichmentChange,
  isLoading,
}) => {
  const enrichmentCategories = ["Consumption", "Interest"];
  const enrichmentTypes = [
    { value: "brand_preferences", display: "Brand Preference" },
    { value: "passion_points", display: "Passion Point" },
    { value: "weekly_plus", display: "Weekly Plus" },
  ];

  if (isLoading) {
    return (
      <Box sx={{ textAlign: "center", p: 4 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading form details...
        </Typography>
      </Box>
    );
  }

  if (!formDetails) {
    return (
      <Box sx={{ textAlign: "center", p: 4 }}>
        <Typography variant="body1">
          Please go back and select a form to see its details.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Form Details & Configuration
      </Typography>
      <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
        <Typography variant="subtitle1">
          <strong>Form ID:</strong> {formDetails.id}
        </Typography>
        <Typography variant="subtitle1">
          <strong>Form Name:</strong> {formDetails.name}
        </Typography>
      </Card>
      {formDetails.questions && formDetails.questions.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ mt: 2, display: "flex", alignItems: "center" }}
          >
            Enrichment Questions
            <Tooltip title="Configure how questions should be processed for enrichment">
              <IconButton>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Typography>
          {formDetails.questions
            .filter((q) => q.options && q.options.length > 0)
            .map((question, index) => {
              const enrichment = enrichments.find((e) => e.questionId === question.id);
              if (!enrichment) return null;
              return (
                <Card key={question.id} variant="outlined" sx={{ mb: 3, p: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Question {index + 1}:</strong> {question.label}
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid xs={12} md={6}>
                      <Dropdown
                        label="Category"
                        id={`category-select-${question.id}`}
                        name="category"
                        value={enrichment.category}
                        onChange={(e) =>
                          onEnrichmentChange(question.id, "category", e.target.value)
                        }
                        options={[
                          { value: "", label: "None" },
                          ...enrichmentCategories.map((category) => ({
                            value: category,
                            label: category,
                          })),
                        ]}
                      />
                    </Grid>
                    <Grid xs={12} md={6}>
                      <Dropdown
                        label="Type"
                        id={`type-select-${question.id}`}
                        name="type"
                        value={enrichment.type}
                        onChange={(e) => onEnrichmentChange(question.id, "type", e.target.value)}
                        disabled={!enrichment.category}
                        key={`${question.id}-${enrichment.category}`}
                        options={[
                          { value: "", label: "None" },
                          ...filterEnrichmentTypesByCategory(
                            enrichment.category,
                            enrichmentTypes
                          ).map((type) => ({
                            value: type.value,
                            label: type.display,
                          })),
                        ]}
                      />
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Available Options:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {question.options.map((option, idx) => (
                        <Chip
                          key={idx}
                          label={option.label || option.key}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                </Card>
              );
            })}
        </Box>
      )}
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6" sx={{ display: "flex", alignItems: "center" }}>
            Default Enrichments
            <Tooltip title="Add enrichments that aren't tied to specific form questions">
              <IconButton>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onAddDefaultEnrichment}
            name="Add Default Enrichment"
            size="small"
          />
        </Box>
        {defaultEnrichments.length === 0 ? (
          <Alert severity="info" sx={{ mb: 3 }}>
            No default enrichments added. Click &quot;Add Default Enrichment&quot; to create
            enrichments that apply to all form submissions.
          </Alert>
        ) : (
          defaultEnrichments.map((enrichment) => (
            <Card key={enrichment.id} variant="outlined" sx={{ mb: 3, p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Default Enrichment</strong>
                </Typography>
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => onRemoveDefaultEnrichment(enrichment.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              <Grid container spacing={2}>
                <Grid xs={12} md={3}>
                  <Dropdown
                    label="Category"
                    id={`default-category-${enrichment.id}`}
                    name="category"
                    value={enrichment.category}
                    onChange={(e) =>
                      onDefaultEnrichmentChange(enrichment.id, "category", e.target.value)
                    }
                    options={[
                      { value: "", label: "Select Category" },
                      ...enrichmentCategories.map((category) => ({
                        value: category,
                        label: category,
                      })),
                    ]}
                  />
                </Grid>
                <Grid xs={12} md={3}>
                  <Dropdown
                    label="Type"
                    id={`default-type-${enrichment.id}`}
                    name="type"
                    value={enrichment.type}
                    onChange={(e) =>
                      onDefaultEnrichmentChange(enrichment.id, "type", e.target.value)
                    }
                    disabled={!enrichment.category}
                    options={[
                      { value: "", label: "Select Type" },
                      ...filterEnrichmentTypesByCategory(enrichment.category, enrichmentTypes).map(
                        (type) => ({
                          value: type.value,
                          label: type.display,
                        })
                      ),
                    ]}
                  />
                </Grid>
                <Grid xs={12} md={3}>
                  <TextField
                    label="Value"
                    id={`default-value-${enrichment.id}`}
                    name="value"
                    value={enrichment.value}
                    onChange={(e) =>
                      onDefaultEnrichmentChange(enrichment.id, "value", e.target.value)
                    }
                    placeholder="Enter enrichment value"
                  />
                </Grid>
              </Grid>
            </Card>
          ))
        )}
      </Box>
    </Box>
  );
};

const ReviewStep = ({ provider, account, form, country, enrichments, defaultEnrichments }) => {
  const questionEnrichments = enrichments.filter((e) => e.category && e.type);
  const validDefaultEnrichments = defaultEnrichments.filter((e) => e.category && e.type && e.value);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review Configuration
      </Typography>
      <Card variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1">
          <strong>Provider:</strong> {provider.charAt(0).toUpperCase() + provider.slice(1)}
        </Typography>
        <Typography variant="subtitle1">
          <strong>Account:</strong> {account}
        </Typography>
        <Typography variant="subtitle1">
          <strong>Form:</strong> {form}
        </Typography>
        <Typography variant="subtitle1">
          <strong>Country:</strong> {country}
        </Typography>
      </Card>
      <Typography variant="h6" gutterBottom>
        Question-based Enrichments
      </Typography>
      {questionEnrichments.length > 0 ? (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "33%" }}>Question ID</TableCell>
                <TableCell sx={{ width: "33%" }}>Category</TableCell>
                <TableCell sx={{ width: "33%" }}>Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {questionEnrichments.map((enrichment) => (
                <TableRow key={enrichment.questionId}>
                  <TableCell>{enrichment.questionId}</TableCell>
                  <TableCell>{enrichment.category}</TableCell>
                  <TableCell>{enrichment.type}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          No question-based enrichment configurations have been defined.
        </Alert>
      )}
      <Typography variant="h6" gutterBottom>
        Default Enrichments
      </Typography>
      {validDefaultEnrichments.length > 0 ? (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "33%" }}>Category</TableCell>
                <TableCell sx={{ width: "33%" }}>Type</TableCell>
                <TableCell sx={{ width: "34%" }}>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {validDefaultEnrichments.map((enrichment) => (
                <TableRow key={enrichment.id}>
                  <TableCell>{enrichment.category}</TableCell>
                  <TableCell>{enrichment.type}</TableCell>
                  <TableCell>
                    <Chip
                      label={enrichment.value}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          No default enrichments have been configured.
        </Alert>
      )}
      <Alert severity="info" sx={{ mt: 3 }}>
        Please review the configuration details above. Click Submit to confirm and save this
        configuration.
      </Alert>
    </Box>
  );
};

const SuccessStep = ({ onReset }) => {
  return (
    <Box sx={{ textAlign: "center", p: 4 }}>
      <CheckIcon color="success" sx={{ fontSize: 60 }} />
      <Typography variant="h5" sx={{ mt: 2 }}>
        Configuration Submitted Successfully!
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
        Your configuration has been saved and is ready to use.
      </Typography>
      <Button
        variant="contained"
        color="primary"
        onClick={onReset}
        sx={{ mt: 2 }}
        name="Create Another Configuration"
      />
    </Box>
  );
};

export default SocialConfigWizard;
